import { cloneElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CircleCheck } from '@unified-checkout/design-system/icons/index';

import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { EMPTY_VALUE } from '@app/dashboard/constants/shared';
import { SMALL_MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';
import { InfoCard } from '@app/dashboard/components/inner_layout/Cards/InfoCard';
import CopyText from '@app/dashboard/components/inner_layout/CopyText';

const getFailureReason = (message: any): string =>
  (typeof message === 'string' ? message : message?.message) || EMPTY_VALUE;

const Details = ({ singleTransactionDetails }: { singleTransactionDetails: Record<string, any> }) => {
  const { t } = useTranslation();
  const screenWidth = useScreenWidth();

  const isSmallMobile = screenWidth <= SMALL_MOBILE_BREAKPOINT;

  const {
    source_data,
    value_date,
    other_endpoint_reference,
    details,
    integration_name,
    integration_id,
    data: TrnxDataObj,
  } = singleTransactionDetails || {};

  const tooltipPlacement: 'auto' | 'top end' = isSmallMobile ? 'auto' : 'top end';

  const integrationDisplayValue = integration_name ? `${integration_name} (#${integration_id})` : integration_id || EMPTY_VALUE;
    
  const getInstantRefuntPairs = () => {
    const hasInstantRefund = source_data?.instant_refund;

    if (!hasInstantRefund) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.instantRefundEligibility'),
        value: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.eligibility'),
        trailing_icon: cloneElement(CircleCheck, {
          children: cloneElement(CircleCheck.props.children, {
            fill: '#038F6E',
          }),
        }),
      },
    ];
  };

  const getPaymeDetailsPairs = () => {
    const hasPaymeDetails = !!singleTransactionDetails?.payme_details;

    if (!hasPaymeDetails) return [];

    const isMainPaymeLink = singleTransactionDetails.payme_details.branch_name === 'Main PayMe Link';
    const branchName = singleTransactionDetails.payme_details.branch_name || EMPTY_VALUE;
    const purposeOfPayment = singleTransactionDetails.payme_details.purpose_of_payment || EMPTY_VALUE;

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.branchName'),
        value: isMainPaymeLink
          ? t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.mainPaymeLink')
          : branchName,
      },
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.purposeOfPayment'),
        value: purposeOfPayment,
      },
    ];
  };

  const getFailureReasonPairs = () => {
    const hasFailureReason = !singleTransactionDetails?.success && !singleTransactionDetails?.pending;

    if (!hasFailureReason) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.failureReason'),
        value: getFailureReason(TrnxDataObj?.message),
      },
    ];
  };

  const getRRNPairs = () => {
    const RRNValue =
      TrnxDataObj?.migs_transaction?.receipt ||
      TrnxDataObj?.extra_detail?.receipt ||
      TrnxDataObj?.processing?.retrieval_reference_number ||
      EMPTY_VALUE;

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.rrn'),
        toolTipProps: {
          text: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.rrnTooltip'),
          placement: tooltipPlacement,
          centerArrow: true
        },
        render: () => <CopyText textToCopy={RRNValue} showTextToCopy />,
      },
    ];
  };

  const getARNValuePairs = () => {
    const valueToCopy = TrnxDataObj?.migs_transaction?.acquirer_transaction_id || TrnxDataObj?.arn || EMPTY_VALUE;

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.arn'),
        toolTipProps: {
          text: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.arnTooltip'),
          placement: tooltipPlacement,
          centerArrow: true,
        },
        render: () => <CopyText textToCopy={valueToCopy} showTextToCopy />,
      },
    ];
  };

  const getAuthorizationCodePairs = () => {
    const authorizationCode = TrnxDataObj?.migs_transaction?.authorizationCode || EMPTY_VALUE;

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.authorizationCode'),
        value: authorizationCode,
      },
    ];
  };

  const getECIValuePairs = () => {
    const eciValue =
      TrnxDataObj.acs_eci ||
      TrnxDataObj.acs_eci === null ||
      TrnxDataObj?.source?.eci ||
      TrnxDataObj?.source?.['payload.source']?.eci;

    if (!eciValue) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.eci'),
        value: eciValue,
      },
    ];
  };

  const getTokenizedTransactionPairs = () => {
    const isCard = source_data?.type === 'card';

    if (!isCard) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.tokenizedTransaction'),
        value: t(`innerLayout.common.${source_data.tokenized_transaction ? 'yes' : 'no'}`),
      },
    ];
  };

  const getEstimatedArrivalFundsPair = () => {
    const isUAE = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY) === COUNTRIES.ARE;

    if (!isUAE) return [];

    return [
      {
        label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.estimatedArrivalFunds'),
        value: value_date ? formatToISODate(value_date, 'dd MMM yyyy') : EMPTY_VALUE,
      },
    ];
  };

  return (
    <InfoCard
      title={t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.title')}
      noOfColumns={2}
      pairsList={[
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.transactionId'),
          value: details.transaction_id || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.orderId'),
          value: details.order_id || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.merchantOrderId'),
          value: details.merchant_order_id || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.type'),
          value: details.type || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.channel'),
          value: details.channel || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.terminal'),
          value: details.terminal || EMPTY_VALUE,
        },
        ...getInstantRefuntPairs(),
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.createdBy'),
          value: details.created_by || EMPTY_VALUE,
        },
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.integrationId'),
          value: integrationDisplayValue,
        },
        ...getPaymeDetailsPairs(),
        ...getFailureReasonPairs(),
        ...getRRNPairs(),
        ...getARNValuePairs(),
        ...getAuthorizationCodePairs(),
        ...getECIValuePairs(),
        ...getTokenizedTransactionPairs(),
        ...getEstimatedArrivalFundsPair(),
        {
          label: t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.otherReferences'),
          value: other_endpoint_reference || EMPTY_VALUE,
        },
      ]}
    />
  );
};

export default Details;
